/**
 * Meanbook Note - Carrier Detection and Formatting Module
 */

import { defaultCarrierSettings } from "./constants.js";
import { escapeHTML } from "./uiHelpers.js";

export function getCarrierSettings() {
  try {
    const saved = localStorage.getItem("carrierSettings");
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error("Failed to load carrier settings", e);
  }
  return defaultCarrierSettings;
}

export function detectCarrier(trackingNumber) {
  if (!trackingNumber) return null;
  const trimmed = trackingNumber.trim().toUpperCase();
  const settings = getCarrierSettings();

  for (const prefix of (settings.ems || [])) {
    if (prefix && trimmed.startsWith(prefix.trim().toUpperCase())) return { name: "EMS", icon: "mail", label: "EMS 📬" };
  }
  for (const prefix of (settings.flash || [])) {
    if (prefix && trimmed.startsWith(prefix.trim().toUpperCase())) return { name: "Flash", icon: "zap", label: "Flash ⚡" };
  }
  for (const prefix of (settings.spx || [])) {
    if (prefix && trimmed.startsWith(prefix.trim().toUpperCase())) return { name: "SPX", icon: "shopping-bag", label: "SPX 🧡" };
  }
  for (const prefix of (settings.dhl || [])) {
    if (prefix && trimmed.startsWith(prefix.trim().toUpperCase())) return { name: "DHL", icon: "globe", label: "DHL 🔴" };
  }
  for (const prefix of (settings.kex || [])) {
    if (prefix && trimmed.startsWith(prefix.trim().toUpperCase())) return { name: "KEX", icon: "package", label: "KEX 📦" };
  }
  for (const prefix of (settings.other || [])) {
    if (prefix && trimmed.startsWith(prefix.trim().toUpperCase())) return { name: "อื่นๆ", icon: "truck", label: "อื่นๆ 🌐" };
  }

  // Fallback patterns based on standard tracking number formats
  if (/^[A-Z]{2}\d{9}[A-Z]{2}$/.test(trimmed)) return { name: "EMS", icon: "mail", label: "EMS 📬" };
  if (/^TH\d{12}$/.test(trimmed)) return { name: "Flash", icon: "zap", label: "Flash ⚡" };
  if (/^SPXTH\d{10,15}$/.test(trimmed)) return { name: "SPX", icon: "shopping-bag", label: "SPX 🧡" };
  if (/^(KEX|KER)\d{9,12}$/.test(trimmed)) return { name: "KEX", icon: "package", label: "KEX 📦" };

  return null;
}

export function detectCarriers(trackingNumber) {
  if (!trackingNumber) return [];
  const trimmed = trackingNumber.trim().toUpperCase();
  const settings = getCarrierSettings();
  const matches = [];

  const carrierList = [
    { name: "EMS", key: "ems", icon: "mail", label: "EMS 📬" },
    { name: "Flash", key: "flash", icon: "zap", label: "Flash ⚡" },
    { name: "SPX", key: "spx", icon: "shopping-bag", label: "SPX 🧡" },
    { name: "DHL", key: "dhl", icon: "globe", label: "DHL 🔴" },
    { name: "KEX", key: "kex", icon: "package", label: "KEX 📦" },
    { name: "อื่นๆ", key: "other", icon: "truck", label: "อื่นๆ 🌐" }
  ];

  for (const carrier of carrierList) {
    if (carrier.key === "other") continue;
    const prefixes = settings[carrier.key] || [];
    for (const prefix of prefixes) {
      if (prefix && trimmed.startsWith(prefix.trim().toUpperCase())) {
        matches.push(carrier);
        break;
      }
    }
  }

  // Fallback patterns based on standard tracking number formats
  if (matches.length === 0) {
    if (/^[A-Z]{2}\d{9}[A-Z]{2}$/.test(trimmed)) {
      matches.push({ name: "EMS", key: "ems", icon: "mail", label: "EMS 📬" });
    } else if (/^TH\d{12}$/.test(trimmed)) {
      matches.push({ name: "Flash", key: "flash", icon: "zap", label: "Flash ⚡" });
    } else if (/^SPXTH\d{10,15}$/.test(trimmed)) {
      matches.push({ name: "SPX", key: "spx", icon: "shopping-bag", label: "SPX 🧡" });
    } else if (/^(KEX|KER)\d{9,12}$/.test(trimmed)) {
      matches.push({ name: "KEX", key: "kex", icon: "package", label: "KEX 📦" });
    }
  }

  return matches;
}

export function updateLiveCarrierBadge(orderId, trackingVal, preSelectedCarrier = null) {
  const detectDiv = document.getElementById(`live-carrier-${orderId}`);
  const input = document.getElementById(`tracking-input-${orderId}`);
  if (!detectDiv || !input) return;

  if (!trackingVal) {
    detectDiv.innerHTML = "พิมพ์หรือวางเลขพัสดุเพื่อตรวจจับขนส่งอัตโนมัติ";
    input.removeAttribute("data-selected-carrier");
    return;
  }

  const matches = detectCarriers(trackingVal);

  if (matches.length === 0) {
    detectDiv.innerHTML = `ตรวจพบขนส่ง: <span style="color: var(--color-text-muted); font-weight: 700; display: inline-flex; align-items: center; gap: 3px;"><i data-lucide="truck" style="width:12px; height:12px;"></i> อื่นๆ 🌐</span>`;
    input.setAttribute("data-selected-carrier", "อื่นๆ");
  } else if (matches.length === 1) {
    const carrier = matches[0];
    detectDiv.innerHTML = `ตรวจพบขนส่ง: <span style="color: #38bdf8; font-weight: 700; display: inline-flex; align-items: center; gap: 3px;"><i data-lucide="${carrier.icon}" style="width:12px; height:12px;"></i> ${carrier.label}</span>`;
    input.setAttribute("data-selected-carrier", carrier.name);
  } else {
    let selectedCarrier = preSelectedCarrier;
    if (!selectedCarrier) {
      selectedCarrier = input.getAttribute("data-selected-carrier");
    }
    if (!selectedCarrier || !matches.some(m => m.name === selectedCarrier)) {
      selectedCarrier = matches[0].name;
    }

    input.setAttribute("data-selected-carrier", selectedCarrier);

    let buttonsHtml = "";
    for (const carrier of matches) {
      const activeClass = carrier.name === selectedCarrier ? "active" : "";
      buttonsHtml += `
        <button type="button" class="btn-carrier-choice ${activeClass}" data-order-id="${orderId}" data-carrier="${carrier.name}">
          ${carrier.label}
        </button>
      `;
    }

    detectDiv.innerHTML = `รหัสซ้ำซ้อน กรุณาเลือกขนส่ง: <div style="display: inline-flex; gap: 4px; align-items: center; margin-left: 4px;">${buttonsHtml}</div>`;
  }

  if (window.lucide) window.lucide.createIcons();
}

export function getCarrierLabelHtml(carrier) {
  if (!carrier) return "เลขพัสดุ 📦";
  switch (carrier.toUpperCase()) {
    case 'EMS': return 'EMS 📬';
    case 'FLASH': return 'Flash ⚡';
    case 'SPX': return 'SPX 🧡';
    case 'DHL': return 'DHL 🔴';
    case 'KEX': return 'KEX 📦';
    case 'อื่นๆ': return 'อื่นๆ 🌐';
    default: return `${escapeHTML(carrier)} 🌐`;
  }
}
